import React from "react";
import { InvoiceItemData, TaxRate } from "../../../../../types/invoices";
import { EDSButton } from "../../../../eds/EDSButton";
import { Plus } from "lucide-react";
import InvoiceItemRow from "./InvoiceItemRow";
import {
  calculateInvoiceGross,
  calculateInvoiceNet,
  calculateInvoiceTax,
  formatDecimal
} from "./invoiceUtils";

interface InvoiceItemsTableProps {
  items: InvoiceItemData[];
  taxRates: TaxRate[];
  vatLabel: string;
  currencySymbol: string;
  isEditing: boolean;
  onUpdateItem: (index: number, updates: Partial<InvoiceItemData>) => void;
  onRemoveItem: (index: number) => void;
  onAddBlankItem: () => void;
}

const InvoiceItemsTable = ({
  items,
  taxRates,
  vatLabel,
  currencySymbol,
  isEditing,
  onUpdateItem,
  onRemoveItem,
  onAddBlankItem
}: InvoiceItemsTableProps): React.JSX.Element => {
  const totalNet = calculateInvoiceNet(items);
  const totalTax = calculateInvoiceTax(items);
  const totalGross = calculateInvoiceGross(items);

  return (
    <div>
      <div className="overflow-x-auto rounded">
        <table className="w-full border-collapse text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <caption className="sr-only">Invoice line items</caption>
          <thead>
            <tr className="border-b-2 border-border">
              <th
                scope="col"
                className="py-2 px-2 text-left font-semibold text-muted-foreground"
              >
                Description
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-right font-semibold text-muted-foreground w-24"
              >
                Net
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-right font-semibold text-muted-foreground w-24"
              >
                {vatLabel}
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-right font-semibold text-muted-foreground w-24"
              >
                Gross
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-left font-semibold text-muted-foreground w-32"
              >
                Tax Rate
              </th>
              {isEditing && (
                <th
                  scope="col"
                  className="py-2 px-2 text-center font-semibold text-muted-foreground w-12"
                >
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <InvoiceItemRow
                key={item.uid}
                item={item}
                index={index}
                taxRates={taxRates}
                currencySymbol={currencySymbol}
                isEditing={isEditing}
                onUpdateItem={onUpdateItem}
                onRemoveItem={onRemoveItem}
              />
            ))}
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={isEditing ? 6 : 5}
                  className="py-4 text-center text-muted-foreground"
                >
                  No invoice items
                </td>
              </tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border font-semibold">
                <td className="py-2 px-2">Totals</td>
                <td className="py-2 px-2 text-right">
                  {currencySymbol}
                  {formatDecimal(totalNet)}
                </td>
                <td className="py-2 px-2 text-right">
                  {currencySymbol}
                  {formatDecimal(totalTax)}
                </td>
                <td className="py-2 px-2 text-right">
                  {currencySymbol}
                  {formatDecimal(totalGross)}
                </td>
                <td className="py-2 px-2" />
                {isEditing && <td className="py-2 px-2" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {isEditing && (
        <div className="flex gap-2 pt-2">
          <EDSButton
            variant="outline"
            size="sm"
            onClick={onAddBlankItem}
            aria-label="Add invoice item"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </EDSButton>
        </div>
      )}
    </div>
  );
};

export default InvoiceItemsTable;
